import { useEffect, useRef, useState } from "react";
import Avatar from "@/components/Avatar";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { PaperPlaneRight, Robot, CheckCircle, Phone, VideoCamera, DotsThreeVertical, Hand, ArrowsClockwise, ArrowDown, InstagramLogo, FacebookLogo, GoogleLogo, UsersThree, Megaphone, Microphone, MagnifyingGlass, Funnel, X, ArrowsDownUp, ChatsCircle, ClockCounterClockwise, Snowflake, Sparkle, CaretRight, Paperclip, Image as ImageIcon, DownloadSimple, FilePdf, FileAudio, FileVideo, File as FileIcon, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const SOURCE_META = {
  instagram_ad: { label: "Instagram (anúncio)", icon: InstagramLogo, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  facebook_ad: { label: "Facebook (anúncio)", icon: FacebookLogo, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  ad_generic: { label: "Anúncio", icon: Megaphone, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  instagram: { label: "Instagram", icon: InstagramLogo, cls: "bg-pink-50 text-pink-700 border-pink-200" },
  facebook: { label: "Facebook", icon: FacebookLogo, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  google: { label: "Google", icon: GoogleLogo, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  referral: { label: "Indicação", icon: UsersThree, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const CHIPS = [
  { code: "",               label: "Todas",            icon: ChatsCircle },
  { code: "awaiting_human", label: "Aguardando humano",icon: Hand },
  { code: "needs_reply",    label: "Sem resposta",     icon: ClockCounterClockwise },
  { code: "unread",         label: "Não lidas",        icon: Sparkle },
  { code: "active_today",   label: "Ativas hoje",      icon: PaperPlaneRight },
  { code: "cold",           label: "Esfriaram",        icon: Snowflake },
  { code: "ai_only",        label: "Só IA",            icon: Robot },
  { code: "mine",           label: "Eu atendi",        icon: Hand },
];

const SORT_OPTIONS = [
  { value: "recent", label: "Recente" },
  { value: "sla",    label: "Sem resp. há +" },
  { value: "name",   label: "Nome A–Z" },
];

const STATE_OPTIONS = [
  { value: "",              label: "Qualquer" },
  { value: "idle",          label: "IA ativa" },
  { value: "human_handoff", label: "Aguardando humano" },
  { value: "waiting_reply", label: "Aguardando cliente" },
  { value: "closed",        label: "Encerrada" },
];

const PERIOD_OPTIONS = [
  { value: "",      label: "Tudo" },
  { value: "today", label: "Hoje" },
  { value: "7d",    label: "Últimos 7 dias" },
  { value: "30d",   label: "Últimos 30 dias" },
  { value: "older", label: "Mais antigas" },
];

function ageLabel(min) {
  // v0.95.20: rótulos explícitos em PT-BR sem colisões visuais.
  // Antes, "1m" (ambíguo: minuto? mês?) causou pânico ao dono do
  // produto vendo conversas antigas. Agora usamos sufixos únicos:
  //   agora / Xmin / Xh / Xd / Xmês / Xmeses / Xano / Xanos
  if (min === null || min === undefined) return null;
  if (min < 1) return "agora";
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? "1mês" : `${months}meses`;
  const years = Math.floor(months / 12);
  return years === 1 ? "1ano" : `${years}anos`;
}

function SourceBadge({ source, campaign }) {
  const meta = SOURCE_META[source];
  if (!meta) return null;
  const Icon = meta.icon;
  // Apple-style: neutral pill, ícone carrega a identidade da fonte.
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border bg-slate-50 text-slate-600 border-slate-200/80 max-w-[140px]"
      data-testid={`customer-source-badge-${source}`}
      title={campaign ? `Campanha: ${campaign}` : meta.label}
    >
      <Icon size={11} weight="fill" className="text-slate-500 shrink-0" />
      <span className="truncate">{meta.label}</span>
      {campaign && <span className="opacity-70 truncate">· {campaign}</span>}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────
// Media bubble helpers
// ────────────────────────────────────────────────────────────────────

const IMAGE_EXT_RX = /\.(png|jpe?g|gif|webp|bmp|heic|heif)(\?|$)/i;
const AUDIO_EXT_RX = /\.(mp3|ogg|m4a|aac|wav|opus|oga)(\?|$)/i;
const VIDEO_EXT_RX = /\.(mp4|mov|webm|3gp|mkv)(\?|$)/i;
const PDF_EXT_RX = /\.pdf(\?|$)/i;

function _kindFromMessage(m) {
  const url = m.meta?.media_url || "";
  if (m.meta?.kind === "image" || IMAGE_EXT_RX.test(url)) return "image";
  if (m.meta?.kind === "audio" || AUDIO_EXT_RX.test(url)) return "audio";
  if (m.meta?.kind === "video" || VIDEO_EXT_RX.test(url)) return "video";
  if (m.meta?.kind === "pdf" || PDF_EXT_RX.test(url) || m.meta?.kind === "document") return "pdf";
  if (url) return "file";
  return null;
}

function _filenameFromMessage(m, kind) {
  // Prefer explicit filename → caption snippet → composed from message id+kind.
  const explicit = m.meta?.file_name || m.meta?.filename;
  if (explicit) return explicit;
  const url = m.meta?.media_url || "";
  try {
    const u = new URL(url);
    const tail = u.pathname.split("/").pop() || "";
    if (tail && tail.includes(".")) return decodeURIComponent(tail);
  } catch { /* not a URL */ }
  const ext = { image: "jpg", audio: "mp3", video: "mp4", pdf: "pdf", file: "bin" }[kind] || "bin";
  const stamp = (m.created_at || "").replace(/[^0-9]/g, "").slice(0, 14) || "media";
  return `agentus-${kind || "anexo"}-${stamp}.${ext}`;
}

async function _downloadMedia(url, filename) {
  // Cross-origin URLs (Z-API, S3) ignore the `download` HTML attribute, so
  // we fetch as Blob and trigger a synthetic anchor click. Falls back to
  // opening in a new tab if the fetch is blocked by CORS.
  try {
    const resp = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
    toast.success("Download iniciado.");
  } catch {
    // Last-resort fallback: open in new tab so the operator can right-click → save.
    window.open(url, "_blank", "noopener");
    toast.info("Abrindo em nova aba — use 'Salvar como' do navegador.");
  }
}

function MediaIconFor(kind) {
  if (kind === "audio") return FileAudio;
  if (kind === "video") return FileVideo;
  if (kind === "pdf") return FilePdf;
  if (kind === "image") return ImageIcon;
  return FileIcon;
}

function Bubble({ m }) {
  const out = m.direction === "out";
  const when = new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const mediaUrl = m.meta?.media_url;
  const kind = mediaUrl ? _kindFromMessage(m) : null;
  const isImage = kind === "image";
  const isAudio = kind === "audio";
  const isVideo = kind === "video";
  // BUGFIX (iter-65): only derive `caption` when there's actually a media
  // attachment. The previous logic fell back to `m.body` even for text-only
  // messages, then the JSX below rendered BOTH `{caption && …}` and
  // `{!mediaUrl && <div>{m.body}</div>}` → identical text printed twice
  // inside the same bubble. For text-only messages we now keep caption
  // empty so only the body block fires.
  const caption = mediaUrl
    ? (m.meta?.caption
        || (m.body
            && m.body !== "[imagem]" && m.body !== "[áudio]"
            && m.body !== "[vídeo]" && m.body !== "[documento]"
            ? m.body
            : ""))
    : "";
  const filename = mediaUrl ? _filenameFromMessage(m, kind) : null;
  const MediaIcon = MediaIconFor(kind);

  const handleDownload = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (mediaUrl) _downloadMedia(mediaUrl, filename);
  };

  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"} my-1`}>
      <div
        className={`relative max-w-[75%] px-3 py-2 rounded-lg shadow-sm text-sm ${
          out
            ? "bg-[#DCF8C6] text-slate-900 rounded-tr-none wa-bubble-out"
            : "bg-white text-slate-900 rounded-tl-none wa-bubble-in"
        }`}
        data-testid={`message-bubble-${m.id}`}
      >
        {isImage && mediaUrl && (
          <div className="relative group">
            <a href={mediaUrl} target="_blank" rel="noreferrer" className="block" data-testid={`media-open-${m.id}`}>
              <img
                src={mediaUrl}
                alt={caption || "imagem"}
                className="rounded-md max-w-full max-h-80 object-cover mb-1.5 bg-slate-100"
                loading="lazy"
              />
            </a>
            <button
              type="button"
              onClick={handleDownload}
              data-testid={`media-download-${m.id}`}
              title="Baixar imagem"
              className="absolute top-2 right-2 p-1.5 rounded-full bg-slate-900/70 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:bg-slate-900"
            >
              <DownloadSimple size={14} weight="bold" />
            </button>
          </div>
        )}

        {isAudio && mediaUrl && (
          <div className="flex flex-col gap-1.5 min-w-[200px]">
            <audio controls src={mediaUrl} className="w-full h-9" data-testid={`media-audio-${m.id}`} />
            <button
              type="button"
              onClick={handleDownload}
              data-testid={`media-download-${m.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-slate-900 self-start"
            >
              <DownloadSimple size={12} weight="bold" /> Baixar áudio
            </button>
          </div>
        )}

        {isVideo && mediaUrl && (
          <div className="flex flex-col gap-1.5">
            <video controls src={mediaUrl} className="rounded-md max-w-full max-h-80 bg-slate-100" data-testid={`media-video-${m.id}`} />
            <button
              type="button"
              onClick={handleDownload}
              data-testid={`media-download-${m.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-slate-900 self-start"
            >
              <DownloadSimple size={12} weight="bold" /> Baixar vídeo
            </button>
          </div>
        )}

        {!isImage && !isAudio && !isVideo && mediaUrl && (
          // Generic file (PDF, doc, anything else)
          <a
            href={mediaUrl}
            onClick={handleDownload}
            data-testid={`media-download-${m.id}`}
            className="flex items-center gap-2.5 p-2 -m-0.5 rounded-md bg-slate-100/60 hover:bg-slate-100 transition-colors mb-1.5"
            title="Baixar arquivo"
          >
            <span className="w-9 h-9 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-700 shrink-0">
              <MediaIcon size={18} weight="regular" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[12.5px] font-medium text-slate-900 truncate">{filename}</span>
              <span className="block text-[11px] text-slate-500 inline-flex items-center gap-1">
                <DownloadSimple size={11} weight="bold" /> Baixar
              </span>
            </span>
          </a>
        )}

        {caption && (
          <div className="whitespace-pre-wrap leading-snug">{caption}</div>
        )}
        {!mediaUrl && (
          m.body ? (
            <div className="whitespace-pre-wrap leading-snug">{m.body}</div>
          ) : (
            // v0.95.36 — Defensive: se body vazio E sem media_url,
            // renderiza marcador visível pro operador ver que existe
            // uma msg problemática (em vez de bolha invisível). Marcelo
            // reportou "não apareceu minha resposta" — sem esse
            // fallback, msgs quebradas somem no chat.
            <div className="whitespace-pre-wrap leading-snug italic text-slate-500 text-xs">
              ⚠️ mensagem sem conteúdo
            </div>
          )
        )}
        <div className="text-[10px] text-slate-500 text-right mt-0.5 flex items-center justify-end gap-1">
          {m.meta?.origin === "operator_mobile" && (
            <span
              data-testid={`bubble-mobile-${m.id}`}
              title="Enviada do celular (WhatsApp do operador)"
              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-slate-500 bg-slate-200/60 px-1 py-px rounded"
            >
              📱 celular
            </span>
          )}
          {when}
          {out && <CheckCircle size={11} weight="fill" className="text-blue-500" />}
        </div>
      </div>
    </div>
  );
}

export default function Conversations() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ total_open: null, awaiting_human: null, needs_reply: null, active_today: null, cold: null });
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [text, setText] = useState("");
  const [agentThinking] = useState(false);
  const [togglingAi, setTogglingAi] = useState(false);
  const [convMenuOpen, setConvMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  const canDeleteConv = user && ["owner", "admin", "super_admin"].includes(user.role);
  // ── Filters state ──
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [chip, setChip] = useState("");
  const [sort, setSort] = useState("recent");
  const [filterOpen, setFilterOpen] = useState(false);
  const [adv, setAdv] = useState({ source: "", state: "", period: "" });
  // Universal customer search (people with NO conversation yet) — populated
  // from /api/conversations/customer-search whenever the operator types 2+
  // chars. We show this as a dropdown ABOVE the regular conversation list
  // so the operator can start a fresh thread with any customer in the base
  // without ever leaving the Conversas screen.
  const [customerHits, setCustomerHits] = useState([]);
  const [openingFor, setOpeningFor] = useState(null);
  // Smart scroll-to-bottom — counts new messages arrived while user is
  // reading history (not anchored at bottom). Shows a floating "↓ N novas"
  // pill, WhatsApp-Web style.
  const [newMsgCount, setNewMsgCount] = useState(0);
  const [showJumpBtn, setShowJumpBtn] = useState(false);
  const nav = useNavigate();
  const { id: routeId } = useParams();
  const bottomRef = useRef(null);
  const chatScrollRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const wasNearBottomRef = useRef(true);

  // True when the chat is scrolled close to the latest message
  // (within 80px). Used both to auto-scroll on new msg arrival and to
  // hide the floating pill.
  const isNearBottom = () => {
    const el = chatScrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const scrollToBottom = (behavior = "smooth") => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    setNewMsgCount(0);
    setShowJumpBtn(false);
  };

  // Track scroll position so the jump-button only shows when the user
  // has actually moved away from the bottom.
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const near = isNearBottom();
      wasNearBottomRef.current = near;
      if (near) {
        setNewMsgCount(0);
        setShowJumpBtn(false);
      } else {
        setShowJumpBtn(true);
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [active]);

  // Debounced search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Universal customer lookup. Hits the lightweight endpoint that returns
  // matching customers regardless of whether they have a conversation. We
  // de-bounce in the same window as the conversation search.
  useEffect(() => {
    let cancelled = false;
    if (debouncedSearch.length < 2) {
      setCustomerHits([]);
      return undefined;
    }
    api.get("/conversations/customer-search", { params: { q: debouncedSearch, limit: 8 } })
      .then(({ data }) => { if (!cancelled) setCustomerHits(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setCustomerHits([]); });
    return () => { cancelled = true; };
  }, [debouncedSearch]);

  const openConversationForCustomer = async (customerId) => {
    if (openingFor) return;
    setOpeningFor(customerId);
    try {
      const { data } = await api.post(`/conversations/open-for-customer/${customerId}`);
      const cid = data?.conversation?.id;
      if (cid) {
        setSearch("");
        setCustomerHits([]);
        await loadList();
        nav(`/app/conversas/${cid}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível abrir a conversa.");
    } finally {
      setOpeningFor(null);
    }
  };

  const loadList = async () => {
    const params = {};
    if (chip) params.chip = chip;
    if (debouncedSearch) params.search = debouncedSearch;
    if (sort) params.sort = sort;
    if (adv.source) params.source = adv.source;
    if (adv.state) params.state = adv.state;
    if (adv.period) params.period = adv.period;
    const { data } = await api.get("/conversations", { params });
    setList(data);
    if (!active && data[0]) {
      const target = routeId ? data.find((d) => d.conversation.id === routeId) : data[0];
      if (target) setActive(target.conversation.id);
    }
    if (routeId) {
      setActive(routeId);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/conversations/stats");
      setStats(data);
    } catch { /* silent */ }
  };

  const openConv = async (cid) => {
    const { data } = await api.get(`/conversations/${cid}`);
    setCustomer(data.customer);
    setConversation(data.conversation);
    setMessages(data.messages);
    // Switching to another conversation = reset everything and snap to
    // bottom. After this the polling effect takes over.
    setNewMsgCount(0);
    setShowJumpBtn(false);
    wasNearBottomRef.current = true;
    lastMessageIdRef.current = data.messages.at(-1)?.id || null;
    setTimeout(() => scrollToBottom("auto"), 80);
  };

  useEffect(() => { loadList(); loadStats(); }, []);
  // Refetch when any filter changes
  useEffect(() => { loadList(); /* eslint-disable-line */ }, [chip, debouncedSearch, sort, adv]);
  useEffect(() => { if (active) { openConv(active); } }, [active]);

  // Poll the open conversation every 5s so new messages flow in without
  // a manual refresh. Keeps the chat feeling alive.
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/conversations/${active}`);
        setMessages(data.messages);
        setConversation(data.conversation);
      } catch {/* swallow */}
    }, 5000);
    return () => clearInterval(t);
  }, [active]);

  // React to message-list changes: if newest message changed and the
  // user is near the bottom, auto-scroll. Otherwise count how many are
  // hidden below the fold so we can show the floating jump pill.
  useEffect(() => {
    if (!messages.length) {
      lastMessageIdRef.current = null;
      return;
    }
    const newestId = messages.at(-1).id;
    if (newestId === lastMessageIdRef.current) return;
    // Find how many appended after the last known id
    const lastIdx = messages.findIndex((m) => m.id === lastMessageIdRef.current);
    const added = lastIdx === -1 ? 1 : messages.length - 1 - lastIdx;
    lastMessageIdRef.current = newestId;

    if (wasNearBottomRef.current) {
      // Defer to next tick so the bubble is in the DOM before we scroll
      requestAnimationFrame(() => scrollToBottom("smooth"));
    } else {
      setNewMsgCount((c) => c + added);
      setShowJumpBtn(true);
    }
  }, [messages]);  // eslint-disable-line

  const toggleAi = async () => {
    if (!active || togglingAi) return;
    const willPause = !conversation?.human_required;
    setTogglingAi(true);
    try {
      await api.post(`/conversations/${active}/toggle_ai`, {
        human_required: willPause,
        reason: willPause ? "operator_takeover" : null,
      });
      toast.success(willPause ? "IA pausada — você está atendendo" : "IA reativada");
      await openConv(active);
    } catch {
      toast.error("Não foi possível alternar o controle");
    } finally {
      setTogglingAi(false);
    }
  };

  const deleteConversation = async () => {
    if (!active || deleting) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/conversations/${active}`);
      toast.success(
        `Conversa excluída · ${data.messages_deleted} ${
          data.messages_deleted === 1 ? "mensagem removida" : "mensagens removidas"
        }`,
      );
      // Sai da conversa, limpa estado, recarrega lista
      setActive(null);
      setMessages([]);
      setCustomer(null);
      setConversation(null);
      setConfirmDelete(false);
      setConvMenuOpen(false);
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível excluir a conversa.");
    } finally {
      setDeleting(false);
    }
  };

  const sendAgent = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post(`/conversations/${active}/send`, { body: text });
      setText("");
      openConv(active);
      loadList();
    } catch { toast.error("Falha"); }
  };

  // Image upload (operator-only, when human takeover is on). We read the
  // file as a base64 data URI inside the browser and ship it to the
  // backend, which forwards to Z-API `/send-image`. We don't host any
  // intermediate file storage — the image lives inside the message meta
  // as a data URI for re-display in the chat surface.
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const openImagePicker = () => fileInputRef.current?.click();
  const sendAgentImage = async (file) => {
    if (!file || !active) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione uma imagem");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem maior que 5MB — comprima antes");
      return;
    }
    setUploadingImage(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      await api.post(`/conversations/${active}/send-image`, {
        image_data: dataUrl,
        caption: text || "",
      });
      setText("");
      toast.success("Imagem enviada");
      openConv(active);
      loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar imagem");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const sendAgentAudio = async () => {
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post(`/conversations/${active}/send`, { body: text, as_audio: true });
      const isAudio = data?.meta?.kind === "audio";
      toast.success(isAudio ? "Áudio enviado pelo Concierge" : "Áudio indisponível — enviei texto");
      setText("");
      openConv(active);
      loadList();
    } catch { toast.error("Falha"); }
  };

  return (
    <>
      <Topbar
        title="Conversas"
        subtitle="Conversation Gateway · todas as conversas reais do WhatsApp em tempo real."
      />
      <div className="px-6 py-4 md:px-8 md:py-6" style={{ height: "calc(100dvh - 5rem)" }}>
        <div className="h-full bg-white border border-slate-200 rounded-md overflow-hidden grid grid-cols-1 md:grid-cols-[340px_1fr] lg:grid-cols-[380px_1fr]">
          {/* Sidebar list — Apple-style operational filters */}
          <aside className="border-r border-slate-200 overflow-hidden flex flex-col" data-testid="conversations-list">
            {/* Search + Filter + Sort */}
            <div className="p-3 border-b border-slate-100 space-y-2.5">
              <div className="relative">
                <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar nome, telefone ou conteúdo…"
                  data-testid="conversations-search"
                  className="w-full rounded-full border border-slate-200 bg-slate-50/60 pl-7 pr-2.5 py-1.5 text-[12.5px] focus:outline-none focus:border-slate-900 focus:bg-white"
                />
                {/* Universal customer dropdown — appears when the operator
                    types 2+ chars and shows ALL matching customers,
                    including those that have NO conversation yet. Clicking
                    a customer here opens (or reuses) a conversation. */}
                {customerHits.length > 0 && debouncedSearch.length >= 2 && (
                  <div
                    className="absolute z-30 left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-lg shadow-[0_12px_28px_-12px_rgba(15,23,42,0.18)] overflow-hidden"
                    data-testid="conversations-customer-search-results"
                  >
                    <div className="px-3 py-2 text-[9.5px] uppercase tracking-[0.16em] font-semibold text-slate-400 bg-slate-50 border-b border-slate-100">
                      Clientes da sua base
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {customerHits.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            if (c.open_conversation_id) {
                              nav(`/app/conversas/${c.open_conversation_id}`);
                              setSearch("");
                              setCustomerHits([]);
                            } else {
                              openConversationForCustomer(c.id);
                            }
                          }}
                          disabled={openingFor === c.id}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 transition-colors text-left disabled:opacity-50"
                          data-testid={`conversations-customer-hit-${c.id}`}
                        >
                          <Avatar name={c.name} src={c.avatar_url} size={28} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[12.5px] font-medium text-slate-900 truncate">{c.name || "Sem nome"}</span>
                              {c.tier === "vip" && (
                                <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-white bg-slate-900 px-1 py-px rounded">VIP</span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">{c.phone || "—"}</div>
                          </div>
                          <span className="text-[10.5px] text-slate-500 shrink-0">
                            {openingFor === c.id
                              ? "abrindo…"
                              : c.open_conversation_id ? "ir para conversa" : "iniciar conversa"}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <div className="relative flex-1">
                  <ArrowsDownUp size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    data-testid="conversations-sort"
                    className="w-full rounded-full border border-slate-200 bg-white pl-6 pr-6 py-1 text-[11.5px] font-medium text-slate-700 focus:outline-none focus:border-slate-900 appearance-none cursor-pointer"
                  >
                    {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  data-testid="conversations-filters-btn"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 text-[11.5px] font-medium text-slate-700"
                >
                  <Funnel size={11} weight="bold" />
                  Filtros
                  {(adv.source || adv.state || adv.period) && (
                    <span className="ml-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-900 text-white text-[9px] font-bold">
                      {[adv.source, adv.state, adv.period].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>
              {/* Tiny counter pills */}
              <div className="grid grid-cols-3 gap-1 text-[10px]" data-testid="conversations-stats">
                <CounterPill label="Aguardando" value={stats.awaiting_human} active={chip === "awaiting_human"} testid="stat-awaiting" />
                <CounterPill label="Sem resp." value={stats.needs_reply} active={chip === "needs_reply"} testid="stat-needs-reply" />
                <CounterPill label="Hoje" value={stats.active_today} active={chip === "active_today"} testid="stat-today" />
              </div>
            </div>
            {/* Chips — flex-wrap em 2 linhas (em sidebar estreita o scroll dá sensação de "corte") */}
            <div className="px-2.5 py-2 border-b border-slate-100" data-testid="conversations-chips">
              <div className="flex flex-wrap items-center gap-1.5">
                {CHIPS.map((c) => {
                  const Icon = c.icon;
                  const isActive = chip === c.code;
                  return (
                    <button
                      key={c.code || "all"}
                      onClick={() => setChip(c.code)}
                      data-testid={`chip-${c.code || "all"}`}
                      className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold whitespace-nowrap transition-all duration-200 ${
                        isActive
                          ? "bg-slate-900 text-white border-slate-900 shadow-sm shadow-slate-900/15"
                          : "bg-white text-slate-700 border-slate-200/80 hover:border-slate-400 hover:bg-slate-50"
                      }`}
                    >
                      <Icon size={10} weight="fill" /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* List items */}
            <div className="flex-1 overflow-y-auto chat-scroll">
            {list.length === 0 && (
              <div className="p-8 text-center" data-testid="conversations-empty">
                <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 text-slate-600 flex items-center justify-center mb-3">
                  <ChatsCircle size={20} weight="regular" />
                </div>
                <div className="text-slate-900 font-medium text-sm mb-1">
                  {chip || debouncedSearch || adv.source || adv.state || adv.period
                    ? "Nada encontrado pra esse filtro"
                    : "Aguardando o primeiro cliente"}
                </div>
                <p className="text-xs text-slate-500 leading-relaxed max-w-[240px] mx-auto">
                  {chip || debouncedSearch || adv.source || adv.state || adv.period
                    ? "Tente limpar os filtros ou outra busca."
                    : "Quando alguém mandar uma mensagem no WhatsApp do seu negócio, a conversa aparece aqui em tempo real."}
                </p>
                {!chip && !debouncedSearch && (
                  <button onClick={() => nav("/app/integracoes")} className="mt-4 text-[12px] text-slate-700 hover:text-slate-900 underline underline-offset-2" data-testid="conversations-empty-cta">
                    Conferir conexão do WhatsApp →
                  </button>
                )}
              </div>
            )}
            {list.map((c) => {
              const isHumanRequired = c.conversation.human_required;
              const isClosed = c.conversation.status === "closed";
              const showSlaPill = !isClosed && c.needs_reply && c.age_minutes != null;
              // "Precisa atenção" = última msg do cliente sem resposta há +1h
              // OU cliente pediu human takeover. Mostramos um badge numérico
              // ao lado do avatar pra dar visibilidade imediata pra quem
              // está atendendo.
              const unread = c.unread_count || 0;
              const needsAttention = !isClosed && (c.needs_reply || isHumanRequired);
              const isActiveOpen = active === c.conversation.id;
              const attentionCount = unread > 0 ? unread : (needsAttention ? 1 : 0);
              return (
              <button
                key={c.conversation.id}
                onClick={() => setActive(c.conversation.id)}
                data-testid={`conversation-item-${c.conversation.id}`}
                className={`w-full text-left p-3.5 border-b border-slate-100 hover:bg-slate-50/70 transition-colors ${isActiveOpen ? "bg-slate-100/70" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    <Avatar
                      name={c.customer_name}
                      url={c.customer_avatar_url}
                      size="md"
                      dim={isClosed}
                    />
                    {attentionCount > 0 && !isActiveOpen && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center tabular-nums shadow-sm ring-2 ring-white"
                        title={isHumanRequired ? "Cliente pediu atendente — precisa de atenção" : "Sem resposta há mais de 1h"}
                        data-testid="conv-list-attention-badge"
                      >
                        {attentionCount > 9 ? "9+" : attentionCount}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-slate-900 truncate flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{c.customer_name}</span>
                        {isHumanRequired && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-slate-900 text-white px-1.5 py-0.5 rounded shrink-0" data-testid="conv-list-human-pill">
                            <Hand size={9} weight="fill" /> humano
                          </span>
                        )}
                        {c.conversation?.assigned_to_name && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-violet-600 text-white px-1.5 py-0.5 rounded shrink-0"
                            title={`Cliente pediu falar com ${c.conversation.assigned_to_name}`}
                            data-testid="conv-list-assigned-pill"
                          >
                            → {c.conversation.assigned_to_name.split(" ")[0]}
                          </span>
                        )}
                      </div>
                      {showSlaPill && (
                        <span
                          className={`text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 tabular-nums font-semibold ${
                            c.age_minutes >= 60 * 24 ? "bg-slate-900 text-white"
                            : c.age_minutes >= 60 * 4 ? "bg-slate-200 text-slate-900"
                            : "bg-slate-100 text-slate-700"
                          }`}
                          title={`Última mensagem do cliente há ${ageLabel(c.age_minutes)}`}
                          data-testid="conv-list-sla-pill"
                        >
                          ⏱ {ageLabel(c.age_minutes)}
                        </span>
                      )}
                      {!showSlaPill && !isClosed && c.age_minutes != null && (
                        <span className="text-[10px] text-slate-400 shrink-0 tabular-nums">{ageLabel(c.age_minutes)}</span>
                      )}
                    </div>
                    {c.customer_source && c.customer_source !== "direct" && (
                      <div className="mt-1">
                        <SourceBadge source={c.customer_source} campaign={c.customer_source_campaign} />
                      </div>
                    )}
                    <div className="text-xs text-slate-500 truncate mt-0.5 flex items-center gap-1">
                      {c.last_direction === "in" && <span className="text-slate-400 shrink-0">›</span>}
                      <span className="truncate">{c.last_message || "—"}</span>
                    </div>
                  </div>
                </div>
              </button>
              );
            })}
            </div>
          </aside>

          {/* Chat */}
          <section className="flex flex-col min-w-0 min-h-0">
            {active ? (
              <>
                {/* WA Header */}
                <header className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
                  {customer?.avatar_url ? (
                    <img
                      src={customer.avatar_url}
                      alt={customer.name || "avatar"}
                      className="w-9 h-9 rounded-full object-cover bg-white/10"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      data-testid="chat-header-avatar"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center font-semibold" data-testid="chat-header-initial">
                      {(customer?.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      <span>{customer?.name || "—"}</span>
                      {customer?.meta?.source && customer.meta.source !== "direct" && (
                        <SourceBadge source={customer.meta.source} campaign={customer.meta.source_campaign} />
                      )}
                    </div>
                    <div className="text-xs opacity-80 font-mono flex items-center gap-2 flex-wrap">
                      <span>{customer?.phone}</span>
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                          conversation?.human_required
                            ? "bg-orange-500/90 text-white"
                            : "bg-emerald-500/90 text-white"
                        }`}
                        data-testid="conversation-ai-status-pill"
                      >
                        {conversation?.human_required ? (
                          <><Hand size={10} weight="fill" /> Você atendendo</>
                        ) : (
                          <><Robot size={10} weight="fill" /> IA ativa</>
                        )}
                      </span>
                      {conversation?.assigned_to_name ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-violet-500/90 text-white"
                          data-testid="conversation-assigned-badge"
                          title={`Cliente pediu falar com ${conversation.assigned_to_name}`}
                        >
                          Atribuída a {conversation.assigned_to_name.split(" ")[0]}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <button
                    onClick={toggleAi}
                    disabled={togglingAi}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors disabled:opacity-60 shadow-sm ${
                      conversation?.human_required
                        ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                        : "bg-white hover:bg-slate-50 text-slate-900 border border-white/40"
                    }`}
                    data-testid="conversation-toggle-ai-btn"
                    title={conversation?.human_required ? "Devolver atendimento à IA" : "Pausar a IA e assumir esta conversa"}
                  >
                    {conversation?.human_required ? (
                      <><ArrowsClockwise size={13} weight="bold" /> Devolver à IA</>
                    ) : (
                      <><Hand size={13} weight="bold" /> Pausar IA e assumir</>
                    )}
                  </button>
                  <VideoCamera size={18} className="opacity-80" />
                  <Phone size={18} className="opacity-80" />
                  {/* Menu de ações da conversa (3 dots). Hoje só tem
                      "Excluir conversa" — adicione novas opções aqui
                      mantendo a divisão entre ações destrutivas (em
                      vermelho) e não-destrutivas. */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setConvMenuOpen((v) => !v)}
                      onBlur={() => setTimeout(() => setConvMenuOpen(false), 180)}
                      className="p-1 rounded-full hover:bg-white/10 transition-colors"
                      data-testid="conv-menu-btn"
                      title="Mais opções"
                      aria-label="Mais opções"
                    >
                      <DotsThreeVertical size={18} className="opacity-80" />
                    </button>
                    {convMenuOpen && (
                      <div
                        data-testid="conv-menu"
                        className="absolute right-0 top-full mt-1 w-56 bg-white text-slate-800 border border-slate-200 rounded-lg shadow-lg py-1 z-30"
                      >
                        {canDeleteConv ? (
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setConvMenuOpen(false); setConfirmDelete(true); }}
                            data-testid="conv-menu-delete"
                            className="w-full text-left px-3.5 py-2 text-[13px] text-rose-600 hover:bg-rose-50 inline-flex items-center gap-2"
                          >
                            <Trash size={14} weight="bold" /> Excluir conversa
                          </button>
                        ) : (
                          <div className="px-3.5 py-2 text-[12px] text-slate-500">
                            Apenas o dono/admin pode excluir conversas.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </header>
                {/* Messages — wrapped in a relative box so the
                    floating "↓ N novas" pill can position above the
                    scroll without being clipped by it. */}
                <div className="flex-1 min-h-0 relative">
                  <div
                    ref={chatScrollRef}
                    className="absolute inset-0 overflow-y-auto wa-bg px-4 py-3 chat-scroll"
                    data-testid="chat-messages"
                  >
                  {conversation?.human_required && (
                    <div
                      className="sticky top-0 z-10 -mx-4 -mt-3 mb-3 px-4 py-2.5 bg-emerald-600 text-white flex items-center justify-between gap-3 shadow"
                      data-testid="human-takeover-banner"
                    >
                      <div className="flex items-center gap-2 text-[12px] font-medium">
                        <Hand size={13} weight="fill" /> Você está atendendo essa conversa · IA pausada
                      </div>
                      <button
                        onClick={toggleAi}
                        disabled={togglingAi}
                        data-testid="human-takeover-banner-return"
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-emerald-700 text-[11px] font-semibold hover:bg-emerald-50 transition-colors disabled:opacity-60 whitespace-nowrap"
                      >
                        <ArrowsClockwise size={11} weight="bold" /> Devolver à IA
                      </button>
                    </div>
                  )}
                  {messages.map((m) => <Bubble key={m.id} m={m} />)}
                  {agentThinking && (
                    <div className="flex justify-end my-1">
                      <div className="bg-[#DCF8C6] rounded-lg px-3 py-2 text-xs text-slate-600 italic flex items-center gap-1">
                        <Robot size={12} /> agent pensando…
                      </div>
                    </div>
                  )}
                  <div ref={bottomRef} />
                  </div>
                  {/* Floating jump-to-bottom pill — WhatsApp-Web style.
                      Only shown when there are new messages below the
                      current viewport AND the user has scrolled away
                      from the bottom. */}
                  {showJumpBtn && (
                    <button
                      onClick={() => scrollToBottom("smooth")}
                      data-testid="chat-jump-to-bottom"
                      className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-900 text-white text-[12px] font-medium shadow-lg shadow-slate-900/20 hover:bg-slate-800 transition-colors animate-in fade-in-0 zoom-in-95"
                    >
                      <ArrowDown size={12} weight="bold" />
                      {newMsgCount > 0 ? (
                        <span data-testid="chat-jump-counter">
                          {newMsgCount} {newMsgCount === 1 ? "nova mensagem" : "novas mensagens"}
                        </span>
                      ) : (
                        <span>Ir para o final</span>
                      )}
                    </button>
                  )}
                </div>
                {/* Inputs */}
                <div className="border-t border-slate-200 bg-white">
                  {conversation?.human_required ? (
                    <form onSubmit={sendAgent} className="flex items-center gap-2 px-4 py-3" data-testid="chat-agent-form">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => sendAgentImage(e.target.files?.[0])}
                        data-testid="chat-agent-image-file"
                      />
                      <button
                        type="button"
                        onClick={openImagePicker}
                        disabled={uploadingImage}
                        title="Enviar imagem"
                        className="w-9 h-9 rounded-full bg-white border border-slate-200 text-slate-700 flex items-center justify-center hover:bg-slate-50 disabled:opacity-50"
                        data-testid="chat-agent-send-image"
                      >
                        {uploadingImage ? (
                          <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
                        ) : (
                          <ImageIcon size={16} weight="regular" />
                        )}
                      </button>
                      <input
                        autoFocus
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Você está atendendo — digite a mensagem ao cliente…"
                        className="flex-1 rounded-full bg-slate-100 px-4 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                        data-testid="chat-agent-input"
                      />
                      <button
                        type="button"
                        onClick={sendAgentAudio}
                        disabled={!text.trim()}
                        title="Enviar como áudio (Concierge)"
                        className="w-9 h-9 rounded-full bg-white border border-slate-200 text-slate-700 flex items-center justify-center hover:bg-slate-50 disabled:opacity-50"
                        data-testid="chat-agent-send-audio"
                      >
                        <Microphone size={16} weight="fill" />
                      </button>
                      <button
                        type="submit"
                        disabled={!text.trim()}
                        className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-slate-800 disabled:opacity-50"
                        data-testid="chat-agent-send"
                      >
                        <PaperPlaneRight size={16} weight="fill" />
                      </button>
                    </form>
                  ) : (
                    <div
                      className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50"
                      data-testid="chat-ai-active-notice"
                    >
                      <div className="flex items-center gap-2 text-[12px] text-slate-600">
                        <Robot size={14} weight="fill" className="text-blue-600" />
                        <span>A IA está respondendo automaticamente. Pra entrar na conversa, clique em <strong className="text-slate-900">"Pausar IA e assumir"</strong>.</span>
                      </div>
                      <button
                        onClick={toggleAi}
                        disabled={togglingAi}
                        data-testid="chat-takeover-inline-btn"
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60 whitespace-nowrap"
                      >
                        <Hand size={11} weight="bold" /> Assumir
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Selecione uma conversa</div>
            )}
          </section>
        </div>
      </div>

      {filterOpen && (
        <FiltersModal
          values={adv}
          onChange={setAdv}
          onClose={() => setFilterOpen(false)}
          onClear={() => setAdv({ source: "", state: "", period: "" })}
        />
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !deleting && setConfirmDelete(false)}
          data-testid="conv-delete-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="conv-delete-modal"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                <Warning size={20} weight="fill" className="text-rose-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-[18px] font-semibold text-slate-900 leading-tight">
                  Excluir esta conversa?
                </h3>
                <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed">
                  Vamos apagar <strong>todo o histórico</strong> da conversa com{" "}
                  <strong>{customer?.name || customer?.phone || "este cliente"}</strong> —
                  mensagens, anexos e perguntas pendentes vinculadas. Esta ação{" "}
                  <strong>não dá pra desfazer</strong>.
                </p>
                <ul className="text-[12px] text-slate-500 mt-3 space-y-1 list-disc list-inside">
                  <li>O cliente <strong>continua</strong> na sua base.</li>
                  <li>Agendamentos vinculados <strong>permanecem</strong>.</li>
                  <li>A exclusão fica registrada no log de auditoria.</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                data-testid="conv-delete-cancel"
                className="px-4 py-2 rounded-lg text-[13px] text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={deleteConversation}
                disabled={deleting}
                data-testid="conv-delete-confirm"
                className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {deleting ? (
                  <><ArrowsClockwise size={13} className="animate-spin" /> Excluindo…</>
                ) : (
                  <><Trash size={13} weight="bold" /> Sim, excluir</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────
function CounterPill({ label, value, active, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg px-2 py-1 border text-center transition-colors ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white border-slate-200/80 text-slate-700"
      }`}
    >
      <div className="text-[9px] uppercase tracking-wider opacity-80 truncate" title={label}>{label}</div>
      <div className="font-semibold tabular-nums text-[12.5px]">
        {value === null || value === undefined ? "—" : Number(value).toLocaleString("pt-BR")}
      </div>
    </div>
  );
}

function FiltersModal({ values, onChange, onClose, onClear }) {
  const [local, setLocal] = useState(values);
  const apply = () => { onChange(local); onClose(); };
  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-slate-950/55 backdrop-blur-md animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="conversations-filters-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white w-full max-w-sm max-h-[92vh] flex flex-col rounded-t-2xl md:rounded-2xl shadow-2xl shadow-slate-900/30 ring-1 ring-slate-200/60 overflow-hidden animate-in slide-in-from-bottom-4 md:zoom-in-95 duration-200"
      >
        <header className="flex items-start gap-3 px-5 md:px-6 pt-5 md:pt-6 pb-4 border-b border-slate-100">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-slate-100 text-slate-700">
            <Funnel size={18} weight="fill" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-[17px] font-semibold tracking-tight text-slate-900 truncate">Filtros avançados</h3>
            <p className="text-[12.5px] text-slate-500 mt-0.5 line-clamp-2 leading-snug">Os chips do topo já cobrem aguardando humano, sem resposta, ativas hoje, esfriaram. Use aqui para refinar por origem, estado e período.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar" className="shrink-0 -mt-1 -mr-1 w-8 h-8 rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center transition-colors" data-testid="filter-close">
            <X size={16} weight="bold" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-5">
          <section>
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-2">Origem do lead</div>
            <select
              value={local.source}
              onChange={(e) => setLocal({ ...local, source: e.target.value })}
              data-testid="filter-source"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[13.5px] bg-white focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
            >
              <option value="">Qualquer origem</option>
              <option value="instagram_ad">Instagram (anúncio)</option>
              <option value="facebook_ad">Facebook (anúncio)</option>
              <option value="ad_generic">Anúncio</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="google">Google</option>
              <option value="referral">Indicação</option>
              <option value="direct">WhatsApp direto</option>
              <option value="import">Importação</option>
              <option value="manual">Manual</option>
            </select>
          </section>
          <section>
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-2">Estado da conversa</div>
            <select
              value={local.state}
              onChange={(e) => setLocal({ ...local, state: e.target.value })}
              data-testid="filter-state"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[13.5px] bg-white focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
            >
              {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </section>
          <section>
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-2">Período</div>
            <div className="grid grid-cols-2 gap-2">
              {PERIOD_OPTIONS.map((o) => (
                <button
                  key={o.value || "any"}
                  type="button"
                  onClick={() => setLocal({ ...local, period: o.value })}
                  data-testid={`filter-period-${o.value || "any"}`}
                  className={`text-[12.5px] py-2 rounded-xl border transition-all ${
                    local.period === o.value
                      ? "border-slate-900 bg-slate-900 text-white font-semibold shadow-sm shadow-slate-900/15"
                      : "border-slate-200 bg-white hover:border-slate-400 text-slate-700"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </section>
        </div>
        <footer className="bg-slate-50/70 border-t border-slate-100 px-5 md:px-6 py-3.5 flex items-center justify-end gap-2">
          <button onClick={onClear} className="text-[12.5px] font-medium text-slate-500 hover:text-slate-900 mr-auto" data-testid="filter-clear">Limpar tudo</button>
          <button onClick={onClose} className="text-[12.5px] font-medium text-slate-600 px-3 py-1.5 rounded-full hover:bg-slate-100">Cancelar</button>
          <button onClick={apply} data-testid="filter-apply" className="inline-flex items-center px-4 py-1.5 rounded-full bg-slate-900 text-white text-[12.5px] font-semibold hover:bg-slate-800 shadow-sm shadow-slate-900/15 transition-colors">Aplicar</button>
        </footer>
      </div>
    </div>
  );
}
